'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import I18n from 'I18n';
import { fromJS, List, Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import { Fragment } from 'react';
import { renderToString } from 'react-dom/server';
import { getProperty, getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { getPropertyFormatter } from 'reporting-visualizations/formatters/propertyFormatter';
import { HR } from 'foundations-components/transitional/components/HR';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { ColorSwatch } from 'foundations-components/transitional/components/ColorSwatch';
import { getBreakdownField, getBucketField, getCombinedValues, getCountAndAmountLabel, getMetricField, isTable } from '../lib/salesAnalytics/helpers';
import { DATE_FORMATS, DEFAULT_DATE_FORMAT } from '../lib/salesExplorer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AGGREGATION_PROPS = ['dealstage', 'snapshotDate', 'SUM|count', 'SUM|amount_in_home_currency', 'hs_manual_forecast_category', 'breakdownMin', 'breakdownMax'];
const sortBucket = (a, b) => a.localeCompare(b);
const getI18nTextforSnapshots = (text, count) => {
  const SNAPSHOTS_KEY = 'reporting-ui-components.customWidgets.pipelineSnapshots';
  return count ? I18n.text(`${SNAPSHOTS_KEY}.${text}`, count) : I18n.text(`${SNAPSHOTS_KEY}.${text}`);
};
const snapshotDateFormat = date => I18n.moment(date, DEFAULT_DATE_FORMAT).format(DATE_FORMATS.DAY);
const snapshotSummaryLabel = date => I18n.text('reporting-ui-components.customWidgets.pipelineSnapshots.properties.pipeline_summary_date', {
  date: snapshotDateFormat(date)
});
const transformData = (report, response) => {
  const config = report.get('config');
  const data = response.get('breakdowns').map(breakdown => breakdown.set('SUM|count', breakdown.get('count')).delete('count')).toJS();
  const grouping = config.getIn(['dimensions', 1]);
  const allBuckets = ImmutableSet(data.map(({
    [grouping]: bucket
  }) => bucket));
  const allDates = ImmutableSet(data.map(({
    snapshotDate
  }) => snapshotDate));
  const dataMap = data.reduce((collect, _ref) => {
    let {
        [grouping]: bucket,
        snapshotDate: date
      } = _ref,
      values = _objectWithoutPropertiesLoose(_ref, [grouping, "snapshotDate"].map(_toPropertyKey));
    collect[bucket] = collect[bucket] || {};
    collect[bucket][date] = values;
    return collect;
  }, {});
  allBuckets.forEach(bucket => allDates.forEach(date => {
    dataMap[bucket][date] = dataMap[bucket][date] || {
      'SUM|amount_in_home_currency': 0,
      'SUM|count': 0,
      breakdownMin: null,
      breakdownMax: null
    };
  }));
  return dataMap;
};
export const getChartData = (report, response) => {
  const breakdownField = getBreakdownField(report);
  const transformedData = transformData(report, response);
  const chartData = Object.keys(transformedData).sort(sortBucket).reduce((collect, bucket) => [/* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
  ...collect, ...Object.keys(transformedData[bucket]).sort().map(date => Object.assign({
    [breakdownField]: bucket,
    snapshotDate: date
  }, transformedData[bucket][date]))], []);
  return chartData;
};
export const getTableData = (report, response) => {
  const bucketField = getBucketField(report);
  const breakdownField = getBreakdownField(report);
  const amountField = `SUM|${getMetricField(report)}`;
  const breakdowns = response.get('breakdowns').groupBy(data => data.get(bucketField));
  const tableData = breakdowns.reduce((acc, dataValue, dataKey) => {
    const forecastValues = getCombinedValues(dataValue, breakdownField, amountField, 'count');
    return acc.push(forecastValues.set(bucketField, dataKey));
  }, List());
  const tableDataWithTotal = tableData.sortBy(dataMap => dataMap.get('snapshotDate')).map((dateMap, index) => {
    const amountSum = dateMap.filter((value, key) => key.includes('SUM')).toList().reduce((sum, amount) => sum + amount, 0);
    const countSum = dateMap.filter((value, key) => !key.includes('SUM') && key !== 'snapshotDate').toList().reduce((sum, amount) => sum + amount, 0);
    const totalMap = dateMap.set('Total', countSum).set('Total_SUM', amountSum);
    return index < 1 ? totalMap : totalMap.set('deal_activity', snapshotSummaryLabel(dateMap.get('snapshotDate')));
  });
  return tableDataWithTotal;
};
const getQuartileRange = (point, dataset) => {
  if (point.get('amount_in_home_currency') === 'Q1') {
    return ` ( < ${getPropertyFormatter(getProperty(dataset, 'breakdownMax'))(point.get('breakdownMax'))})`;
  }
  if (point.get('amount_in_home_currency') === 'Q4') {
    return ` ( > ${getPropertyFormatter(getProperty(dataset, 'breakdownMin'))(point.get('breakdownMin'))})`;
  }
  return ` (${getPropertyFormatter(getProperty(dataset, 'breakdownMin'))(point.get('breakdownMin'))} - ${getPropertyFormatter(getProperty(dataset, 'breakdownMax'))(point.get('breakdownMax'))})`;
};
const renderTooltipTotal = (tooltipData, dataset) => {
  const total = tooltipData.reduce((acc, point) => {
    acc.amount += point.get('SUM|amount_in_home_currency');
    acc.count += point.get('SUM|count');
    return acc;
  }, {
    count: 0,
    amount: 0
  });
  const count = getPropertyFormatter(getProperty(dataset, 'SUM|count'))(total.count);
  const amount = getPropertyFormatter(getProperty(dataset, 'SUM|amount_in_home_currency'))(total.amount);
  return total.count && total.amount ? /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    }, "hr"), /*#__PURE__*/_jsxs(Flex, {
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexGrow: "1",
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(View, {
          marginInlineEnd: "100",
          children: /*#__PURE__*/_jsx(ColorSwatch, {
            color: "transparent",
            size: 4
          })
        }), /*#__PURE__*/_jsx(Text, {
          color: "temp-future-text-primary-default",
          typography: "detail-100",
          children: I18n.text('reporting-ui-components.tooltip.totals')
        })]
      }), /*#__PURE__*/_jsx(Text, {
        color: "text-core-subtle",
        typography: "heading-25",
        paddingInlineStart: "100",
        children: getCountAndAmountLabel({
          count,
          amount
        })
      })]
    }, "totalsRow")]
  }) : null;
};
export const getTooltipFormatter = (report, dataset) => function () {
  const {
    series: {
      name,
      chart: {
        options: {
          colors
        }
      }
    },
    x
  } = this;
  const grouping = report.getIn(['config', 'dimensions', 1]);
  const date = dataset.getIn(['properties', 'snapshotDate', 'references']).findKey(ref => ref.get('label') === x);
  const bucket = dataset.getIn(['properties', grouping, 'references']).findKey(ref => ref.get('label') === name);
  const data = dataset.get('data').filter(point => point.get('snapshotDate') === date);
  const tooltip = /*#__PURE__*/_jsxs(View, {
    minInlineSize: "150px",
    boxShadow: "100",
    children: [/*#__PURE__*/_jsx(View, {
      backgroundColor: "fill-accent-neutral-default",
      borderStartStartRadius: "100",
      borderStartEndRadius: "100",
      padding: "200",
      children: /*#__PURE__*/_jsx(Text, {
        as: "small",
        color: "text-core-on-fill-default",
        children: getReferenceLabel(dataset, 'snapshotDate', date)
      })
    }), /*#__PURE__*/_jsxs(View, {
      backgroundColor: "text-core-on-fill-default",
      padding: "200",
      children: [data.map((point, i) => {
        const bucketLabel = getReferenceLabel(dataset, grouping, point.get(grouping));
        const quartileRange = grouping === 'amount_in_home_currency' ? getQuartileRange(point, dataset) : '';
        const rowLabel = `${bucketLabel}${quartileRange}`;
        const count = getPropertyFormatter(getProperty(dataset, 'SUM|count'))(point.get('SUM|count'));
        const amount = getPropertyFormatter(getProperty(dataset, 'SUM|amount_in_home_currency'))(point.get('SUM|amount_in_home_currency'));
        return /*#__PURE__*/_jsxs(Flex, {
          justifyContent: "space-between",
          alignItems: "center",
          children: [/*#__PURE__*/_jsxs(Flex, {
            flexGrow: "1",
            alignItems: "center",
            children: [/*#__PURE__*/_jsx(View, {
              marginInlineEnd: "100",
              children: /*#__PURE__*/_jsx(ColorSwatch, {
                color: colors[i % colors.length],
                size: 4
              })
            }), /*#__PURE__*/_jsxs(Text, {
              color: "text-core-subtle",
              typography: bucket === point.get(grouping) ? 'heading-25' : 'detail-100',
              children: [rowLabel, ":"]
            })]
          }), /*#__PURE__*/_jsx(Text, {
            color: "text-core-subtle",
            typography: "heading-25",
            paddingInlineStart: "100",
            children: getCountAndAmountLabel({
              count,
              amount
            })
          })]
        }, i);
      }), renderTooltipTotal(data, dataset)]
    })]
  });
  return renderToString(tooltip);
};
const getAmountReferences = () => fromJS({
  Q1: {
    label: getI18nTextforSnapshots('properties.deal_size.firstQuartile')
  },
  Q2: {
    label: getI18nTextforSnapshots('properties.deal_size.secondQuartile')
  },
  Q3: {
    label: getI18nTextforSnapshots('properties.deal_size.thirdQuartile')
  },
  Q4: {
    label: getI18nTextforSnapshots('properties.deal_size.fourthQuartile')
  }
});
export const addStaticAmountReferences = ({
  report,
  response,
  datasets
}) => {
  return {
    report,
    response,
    datasets: datasets.map(dataset => {
      return dataset.update('properties', properties => properties.set('amount_in_home_currency', ImmutableMap({
        label: getI18nTextforSnapshots('properties.amount_in_home_currency'),
        references: getAmountReferences()
      })));
    })
  };
};
const sortByBreakdown = (breakdownField, breakdownRefs) => (a, b) => {
  return breakdownRefs.getIn([a.get(breakdownField), 'displayOrder']) - breakdownRefs.getIn([b.get(breakdownField), 'displayOrder']);
};
export const sortBreakdowns = ({
  report,
  response,
  datasets
}) => {
  const breakdownField = getBreakdownField(report);
  let breakdowns = datasets.getIn(['primary', 'data'], List());
  const breakdownRefs = datasets.getIn(['primary', 'properties', breakdownField, 'references']);
  if (!isTable(report)) {
    breakdowns = breakdowns.sort(sortByBreakdown(breakdownField, breakdownRefs));
  }
  return {
    report,
    response,
    datasets: datasets.setIn(['primary', 'data'], breakdowns)
  };
};